import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import React from 'react';
import FilterDateField from '../../../components/filters/filter-date-field';
import { UserRole } from '../../../types';
import { ConversationFilterParams } from '../../../types/messaging-types';

interface MessagingFiltersProps {
  filters: ConversationFilterParams;
  onFiltersChange: (filters: ConversationFilterParams) => void;
  userRole: UserRole;
  supervisors?: Array<{ id: number; name: string }>;
  dealers?: Array<{ id: number; name: string }>;
  onInfoClick?: () => void;
}

export const MessagingFilters: React.FC<MessagingFiltersProps> = ({
  filters,
  onFiltersChange,
  userRole,
  supervisors = [],
  dealers = [],
  onInfoClick,
}) => {
  const isDealer = userRole === UserRole.DEALER;
  const isManufacturer = userRole === UserRole.SUPERVISOR || userRole === UserRole.CSR;

  const handleDateRangeChange = (value: string) => {
    const [fromDate, toDate] = value.includes(':') ? value.split(':') : [value, ''];
    onFiltersChange({
      ...filters,
      dateFrom: fromDate || undefined,
      dateTo: toDate || undefined,
    });
  };

  const handleSupervisorChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      supervisorId: value === '' ? undefined : parseInt(value, 10),
    });
  };

  const handleDealerChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      dealerId: value === '' ? undefined : parseInt(value, 10),
    });
  };

  const dateRangeValue =
    filters.dateFrom && filters.dateTo
      ? `${filters.dateFrom}:${filters.dateTo}`
      : filters.dateFrom || filters.dateTo || '';

  const hasActiveFilters =
    !!filters.dateFrom ||
    !!filters.dateTo ||
    filters.supervisorId !== undefined ||
    filters.dealerId !== undefined;

  const handleClearFilters = () => {
    onFiltersChange({
      dateFrom: undefined,
      dateTo: undefined,
      supervisorId: undefined,
      dealerId: undefined,
      searchTerm: filters.searchTerm, // Preserve search term
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      {/* Date Range Filter */}
      <FilterDateField
        label="Date Range"
        value={dateRangeValue}
        onChange={handleDateRangeChange}
        hasValue={!!dateRangeValue}
        defaultToRangeMode={true}
      />

      {/* User Filter (for Dealers) */}
      {isDealer && supervisors.length > 0 && (
        <FormControl fullWidth size="small">
          <InputLabel shrink>Filter by User</InputLabel>
          <Select
            value={filters.supervisorId?.toString() || ''}
            onChange={handleSupervisorChange}
            label="Filter by User"
            displayEmpty
            notched
            renderValue={selected => {
              if (selected === '' || !selected) {
                return <em>All Users</em>;
              }
              const supervisor = supervisors.find(s => s.id.toString() === selected);
              return supervisor?.name || selected;
            }}
          >
            <MenuItem value="">
              <em>All Users</em>
            </MenuItem>
            {supervisors.map(supervisor => (
              <MenuItem key={supervisor.id} value={supervisor.id.toString()}>
                {supervisor.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Dealer Filter (for Manufacturers) */}
      {isManufacturer && dealers.length > 0 && (
        <FormControl fullWidth size="small">
          <InputLabel shrink>Filter by Dealer</InputLabel>
          <Select
            value={filters.dealerId?.toString() || ''}
            onChange={handleDealerChange}
            label="Filter by Dealer"
            displayEmpty
            notched
            renderValue={selected => {
              if (selected === '' || !selected) {
                return <em>All Dealers</em>;
              }
              const dealer = dealers.find(d => d.id.toString() === selected);
              return dealer?.name || selected;
            }}
          >
            <MenuItem value="">
              <em>All Dealers</em>
            </MenuItem>
            {dealers.map(dealer => (
              <MenuItem key={dealer.id} value={dealer.id.toString()}>
                {dealer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          fullWidth
          size="small"
          sx={{ fontSize: '0.75rem', mt: 1 }}
        >
          Clear Filters
        </Button>
      )}

      {/* Info Icon */}
      {onInfoClick && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="View participant information">
            <IconButton onClick={onInfoClick} size="small">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
